import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, fmtDateTime } from '../lib/datetime.js';
import ImportButton from '../components/ImportButton.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';

export default function Devices() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === 'admin';
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', location: '' });
  const [lastCode, setLastCode] = useState(null);
  // Phase-2 mTLS: { name, password } shown once after a cert-profile download.
  const [lastP12, setLastP12] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // device row being edited
  // Advanced toggle — hides the per-device mTLS column + button by default.
  // The recommended path for new deployments is the shared fleet mTLS
  // profile (Settings → Fleet mTLS profile (MDM)); per-device certs are
  // an advanced option for fleet-incompatible setups. Persist the toggle
  // in localStorage so admins who DO use per-device certs don't have to
  // re-enable on every visit.
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try { return localStorage.getItem('iptbc.admin.devices.advanced') === '1'; }
    catch { return false; }
  });
  const toggleAdvanced = () => {
    setShowAdvanced((v) => {
      const next = !v;
      try { localStorage.setItem('iptbc.admin.devices.advanced', next ? '1' : '0'); }
      catch { /* ignore */ }
      return next;
    });
  };

  const refresh = async () => {
    try {
      const r = await api.devices.list();
      setDevices(r.devices);
    } catch (e) {
      setErr(e.message);
    }
  };

  const generateKioskUrl = async (d) => {
    if (d.kiosk_token && !confirm(
      `Regenerate kiosk URL for "${d.name}"?\n\nThe iPad currently using ${d.kiosk_token} will need its Home Screen icon re-deployed via Jamf with the NEW URL before its next launch — the old URL stops working immediately.`
    )) return;
    try {
      const r = await api.devices.kioskToken(d.id);
      await refresh();
      // Pop the new URL in front of the admin so they can copy it.
      const url = kioskUrlFor(r.device);
      if (url && navigator.clipboard) {
        try { await navigator.clipboard.writeText(url); }
        catch { /* clipboard blocked in some Safari contexts — fall through */ }
      }
      alert(`New kiosk URL for ${r.device.name}:\n\n${url}\n\n(Copied to clipboard. Paste into Jamf's Web Clip URL field for this iPad.)`);
    } catch (e) {
      setErr(e.message);
    }
  };

  const copyKioskUrl = async (d) => {
    const url = kioskUrlFor(d);
    if (!url) return;
    if (navigator.clipboard) {
      try { await navigator.clipboard.writeText(url); }
      catch { /* swallow */ }
    }
  };

  const setKioskActive = async (d, active) => {
    if (!active && !confirm(
      `Disable the kiosk URL for "${d.name}"?\n\nThe iPad will keep working until iOS next wipes its data — but it will NOT be able to auto-recover via the URL while disabled. Use this to stop a leaked or shared link, then investigate or Regenerate.`
    )) return;
    try {
      await api.devices.kioskTokenSetActive(d.id, active);
      await refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.devices.create(newForm);
      setLastCode({ name: r.device.name, code: r.device.pairing_code });
      setNewForm({ name: '', location: '' });
      setShowNew(false);
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (id) => {
    if (!confirm('Regenerating will invalidate the existing token. Continue?')) return;
    try {
      const r = await api.devices.regenerate(id);
      setLastCode({ name: r.device.name, code: r.device.pairing_code });
      await refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  const revoke = async (id) => {
    if (!confirm('Revoke this device? The iPad will need to be paired again.')) return;
    try {
      await api.devices.revoke(id);
      await refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  // Phase-2 mTLS: generate a fresh client cert for this device, download
  // the .mobileconfig profile, and surface the PKCS#12 password so the
  // admin can read it out when the iPad prompts during install.
  const issueCertProfile = async (dev) => {
    setErr(null);
    try {
      const { blob, p12Password } = await api.devices.certProfile(dev.id);
      // Trigger a browser download for the .mobileconfig file.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `iptbookingcheck-${dev.name}.mobileconfig`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setLastP12({ name: dev.name, password: p12Password });
      await refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm(
      'Soft-delete this device?\n\n' +
      'The device will disappear from this list and its pairing will be revoked, ' +
      'but ALL historical sessions, bookings, events, and staff-card images stay ' +
      'in the database so usage history remains queryable.'
    )) return;
    try {
      await api.devices.delete(id);
      await refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-cathay-700">Devices</h1>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={toggleAdvanced}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            title="Per-device mTLS certs and expiry — only needed if you're not using the shared fleet profile in Settings."
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced'}
          </button>
          <a
            href={api.exports.devicesCsvUrl()}
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded text-sm"
          >
            Export CSV
          </a>
          {isAdmin && (
            <ImportButton onImport={(f) => api.imports.devices(f)} onAfter={refresh}>
              Import CSV
            </ImportButton>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowNew((v) => !v)}
              className="bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-2 rounded text-sm"
            >
              {showNew ? 'Cancel' : '+ New device'}
            </button>
          )}
        </div>
      </div>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      {lastCode && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm">
          <div className="font-medium text-amber-900">
            Pairing code for <span className="font-mono">{lastCode.name}</span>
          </div>
          <div className="text-3xl font-mono mt-2 tracking-widest text-amber-900">
            {lastCode.code}
          </div>
          <div className="text-amber-700 mt-2">
            Enter this on the iPad's pairing screen. The code expires in 30 minutes and is
            shown <strong>only once</strong>.
          </div>
        </div>
      )}

      {lastP12 && (
        <div className="bg-cyan-50 border border-cyan-200 rounded p-4 text-sm space-y-2">
          <div className="font-medium text-cyan-900">
            mTLS profile downloaded for <span className="font-mono">{lastP12.name}</span>
          </div>
          <div className="text-cyan-900">
            iOS will prompt for a password when the user taps the
            <strong> .mobileconfig</strong> file. Read this aloud or paste it into the
            iPad:
          </div>
          <div className="text-3xl font-mono tracking-widest text-cyan-900 select-all">
            {lastP12.password}
          </div>
          <div className="text-cyan-700 text-xs">
            The PKCS#12 password is shown <strong>only once</strong>. It's not stored
            anywhere on the server. Lose it and you'll need to re-issue the profile.
          </div>
          <button
            onClick={() => setLastP12(null)}
            className="text-xs text-cyan-700 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {showNew && (
        <form onSubmit={create} className="bg-white rounded shadow-sm border p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm text-slate-700">Name (e.g. IPT-A)</span>
              <input
                required
                value={newForm.name}
                onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-sm text-slate-700">Location (optional)</span>
              <input
                value={newForm.location}
                onChange={(e) => setNewForm((f) => ({ ...f, location: e.target.value }))}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
              />
            </label>
          </div>
          <button
            disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
          >
            Create + issue pairing code
          </button>
        </form>
      )}

      <div className="bg-white rounded shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last seen</th>
              <th className="px-3 py-2">Kiosk URL</th>
              {showAdvanced && <th className="px-3 py-2">mTLS profile</th>}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {devices.map((d) => (
              <tr key={d.id}>
                <td className="px-3 py-2 font-medium">{d.name}</td>
                <td className="px-3 py-2 text-slate-600">{d.location || '—'}</td>
                <td className="px-3 py-2">
                  <span
                    className={
                      'px-2 py-0.5 rounded text-xs ' +
                      (d.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : d.status === 'unpaired'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-slate-200 text-slate-700')
                    }
                  >
                    {d.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {d.last_seen_at ? fmtDateTime(d.last_seen_at) : '—'}
                </td>
                <td className="px-3 py-2">
                  <KioskUrlCell
                    device={d}
                    isAdmin={isAdmin}
                    onGenerate={() => generateKioskUrl(d)}
                    onCopy={() => copyKioskUrl(d)}
                    onSetActive={(active) => setKioskActive(d, active)}
                  />
                </td>
                {showAdvanced && (
                  <td className="px-3 py-2">
                    <CertExpiryBadge expiresAt={d.client_cert_expires_at} />
                  </td>
                )}
                <td className="px-3 py-2 text-right space-x-2">
                  {isAdmin ? (
                    <>
                      <button onClick={() => setEditing(d)} className="text-slate-700 hover:underline">
                        Edit
                      </button>
                      <button onClick={() => regenerate(d.id)} className="text-cathay-700 hover:underline">
                        Regenerate code
                      </button>
                      {showAdvanced && d.status !== 'revoked' && (
                        <button
                          onClick={() => issueCertProfile(d)}
                          className="text-cyan-700 hover:underline"
                          title="Issue a per-device mTLS profile. For most deployments use the shared Fleet mTLS profile in Settings instead."
                        >
                          mTLS profile
                        </button>
                      )}
                      {d.status === 'active' && (
                        <button onClick={() => revoke(d.id)} className="text-amber-700 hover:underline">
                          Revoke
                        </button>
                      )}
                      <button onClick={() => remove(d.id)} className="text-red-600 hover:underline">
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400">read-only</span>
                  )}
                </td>
              </tr>
            ))}
            {devices.length === 0 && (
              <tr>
                <td colSpan={showAdvanced ? 7 : 6} className="px-3 py-6 text-center text-slate-500">
                  No devices yet. Create one to issue an iPad pairing code.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DeviceIpSources devices={devices} />

      <IpAccessControl isAdmin={isAdmin} />

      {editing && (
        <EditDeviceModal
          device={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

// A device showing this many distinct IPs within the churn window is
// flagged — iPad IPs are normally stable, so churn suggests the kiosk
// URL has been copied to another device.
const IP_CHURN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const IP_CHURN_THRESHOLD = 3;

// Bottom-of-page panel: every distinct source IP each iPad has signed in
// from, grouped by device. iPad IPs are normally stable; a device with
// several recent distinct IPs is flagged as a possible kiosk-URL theft
// (regenerate the URL). Data comes from GET /devices/ip-log.
function DeviceIpSources({ devices }) {
  const [ipLog, setIpLog] = useState(null); // null = loading
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.devices.ipLog()
      .then((r) => setIpLog(r.ipLog || []))
      .catch((e) => { setErr(e.message); setIpLog([]); });
  }, []);

  const paired = devices.filter((d) => d.status !== 'unpaired');
  const byDevice = new Map();
  for (const row of ipLog || []) {
    if (!byDevice.has(row.device_id)) byDevice.set(row.device_id, []);
    byDevice.get(row.device_id).push(row);
  }

  const now = Date.now();
  const recentDistinct = (rows) =>
    rows.filter((r) => now - new Date(r.last_seen_at).getTime() < IP_CHURN_WINDOW_MS).length;

  return (
    <section className="mt-10">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">iPad sign-in sources (IP history)</h2>
      <p className="text-xs text-slate-500 mb-3">
        Every distinct IP each paired iPad has signed in from. iPad IPs are normally stable —
        a device showing {IP_CHURN_THRESHOLD}+ distinct IPs in the last 7 days is flagged as a
        possible stolen kiosk URL; regenerate that device’s URL above.
      </p>

      {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
      {ipLog === null && <div className="text-sm text-slate-500">Loading IP history…</div>}

      {ipLog !== null && (
        <div className="space-y-4">
          {paired.length === 0 && (
            <div className="text-sm text-slate-500">No paired devices yet.</div>
          )}
          {paired.map((d) => {
            const rows = byDevice.get(d.id) || [];
            const churn = recentDistinct(rows) >= IP_CHURN_THRESHOLD;
            return (
              <div key={d.id} className="rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="font-medium text-slate-800">{d.name}</span>
                  <span className="text-xs text-slate-500">
                    {rows.length} distinct IP{rows.length === 1 ? '' : 's'}
                  </span>
                  {churn && (
                    <span
                      className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 font-medium"
                      title="Several distinct IPs in the last 7 days — the kiosk URL may have been copied to another device. Consider regenerating it."
                    >
                      ⚠ IP churn — possible stolen URL
                    </span>
                  )}
                </div>
                {rows.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-slate-400">No sign-ins recorded yet.</div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 text-xs">
                        <th className="px-3 py-1.5 font-normal">IP</th>
                        <th className="px-3 py-1.5 font-normal">First seen</th>
                        <th className="px-3 py-1.5 font-normal">Last seen</th>
                        <th className="px-3 py-1.5 font-normal">Sign-ins</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((r) => (
                        <tr key={r.ip}>
                          <td className="px-3 py-1.5 font-mono text-xs text-slate-700">{r.ip}</td>
                          <td className="px-3 py-1.5 text-slate-600">{fmtDateTime(r.first_seen_at)}</td>
                          <td className="px-3 py-1.5 text-slate-600">{fmtDateTime(r.last_seen_at)}</td>
                          <td className="px-3 py-1.5 text-slate-600">{r.hit_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// IP access control: an "odd IP" table (logged IPs that are neither
// known nor blocked, with the devices that used them and a one-click
// Block), a managed list of known/trusted IPs, and the blocklist
// (enforced server-side at sign-in / pair / kiosk-bootstrap).
function IpAccessControl({ isAdmin }) {
  const [data, setData] = useState(null); // { knownIps, blockedIps, oddIps }
  const [err, setErr] = useState(null);
  const [newKnown, setNewKnown] = useState({ ip: '', label: '' });

  const load = () =>
    api.devices.ipAccess()
      .then(setData)
      .catch((e) => setErr(e.message));

  useEffect(() => { load(); }, []);

  const block = async (ip) => {
    const reason = prompt(`Block ${ip} from signing in?\n\nOptional reason:`, '');
    if (reason === null) return; // cancelled
    try { await api.devices.blockIp(ip, reason || null); await load(); }
    catch (e) { setErr(e.message); }
  };
  const unblock = async (ip) => {
    if (!confirm(`Unblock ${ip}? Devices on this IP will be able to sign in again.`)) return;
    try { await api.devices.unblockIp(ip); await load(); }
    catch (e) { setErr(e.message); }
  };
  const addKnown = async (e) => {
    e.preventDefault();
    if (!newKnown.ip.trim()) return;
    try { await api.devices.addKnownIp(newKnown.ip.trim(), newKnown.label.trim() || null); setNewKnown({ ip: '', label: '' }); await load(); }
    catch (e2) { setErr(e2.message); }
  };
  const removeKnown = async (ip) => {
    if (!confirm(`Remove ${ip} from the known list? Its connections will show as “odd” again.`)) return;
    try { await api.devices.removeKnownIp(ip); await load(); }
    catch (e) { setErr(e.message); }
  };

  const odd = data?.oddIps || [];
  const known = data?.knownIps || [];
  const blocked = data?.blockedIps || [];

  return (
    <section className="mt-10">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">IP access control</h2>
      <p className="text-xs text-slate-500 mb-3">
        All iPads normally share one egress IP. “Odd” IPs are connections from addresses that are
        neither known nor blocked — review and block any you don’t recognise. Blocked IPs cannot
        sign in, pair, or recover a kiosk URL.
      </p>
      {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
      {data === null && <div className="text-sm text-slate-500">Loading…</div>}

      {data !== null && (
        <div className="space-y-6">
          {/* Odd IPs */}
          <div>
            <h3 className="text-xs font-semibold text-slate-600 mb-1">
              Odd IPs <span className="font-normal text-slate-400">(not known, not blocked)</span>
            </h3>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2">IP</th>
                    <th className="px-3 py-2">Used by</th>
                    <th className="px-3 py-2">Last seen</th>
                    <th className="px-3 py-2">Sign-ins</th>
                    {isAdmin && <th className="px-3 py-2"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {odd.map((o) => (
                    <tr key={o.ip} className="bg-amber-50/40">
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{o.ip}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {(o.devices || []).map((d) => d.name).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{fmtDateTime(o.last_seen_at)}</td>
                      <td className="px-3 py-2 text-slate-600">{o.hit_count}</td>
                      {isAdmin && (
                        <td className="px-3 py-2 whitespace-nowrap">
                          <button onClick={() => block(o.ip)} className="text-red-600 hover:underline mr-3">Block</button>
                          <button onClick={() => api.devices.addKnownIp(o.ip, null).then(load).catch((e) => setErr(e.message))} className="text-green-700 hover:underline">Mark known</button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {odd.length === 0 && (
                    <tr><td colSpan={isAdmin ? 5 : 4} className="px-3 py-4 text-center text-slate-500">No odd IPs — all connections are from known addresses.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Known + Blocked side by side */}
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-1">Known (trusted) IPs</h3>
              {isAdmin && (
                <form onSubmit={addKnown} className="flex gap-2 mb-2">
                  <input
                    value={newKnown.ip}
                    onChange={(e) => setNewKnown((s) => ({ ...s, ip: e.target.value }))}
                    placeholder="IP address"
                    className="border border-slate-300 rounded px-2 py-1 text-sm font-mono w-40"
                  />
                  <input
                    value={newKnown.label}
                    onChange={(e) => setNewKnown((s) => ({ ...s, label: e.target.value }))}
                    placeholder="Label (e.g. CX City L4)"
                    className="border border-slate-300 rounded px-2 py-1 text-sm flex-1"
                  />
                  <button className="px-3 py-1 text-sm rounded bg-cathay-700 text-white hover:bg-cathay-800">Add</button>
                </form>
              )}
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {known.map((k) => (
                  <div key={k.ip} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    <code className="font-mono text-xs text-slate-800">{k.ip}</code>
                    <span className="text-slate-500 flex-1 truncate">{k.label || ''}</span>
                    {isAdmin && <button onClick={() => removeKnown(k.ip)} className="text-slate-500 hover:text-red-600 text-xs">Remove</button>}
                  </div>
                ))}
                {known.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">No known IPs yet. Add your IPT location’s IP so its iPads stop showing as odd.</div>}
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-1">Blocked IPs</h3>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {blocked.map((b) => (
                  <div key={b.ip} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    <code className="font-mono text-xs text-red-800">{b.ip}</code>
                    <span className="text-slate-500 flex-1 truncate">{b.reason || ''}</span>
                    {isAdmin && <button onClick={() => unblock(b.ip)} className="text-slate-500 hover:text-green-700 text-xs">Unblock</button>}
                  </div>
                ))}
                {blocked.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">No blocked IPs.</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EditDeviceModal({ device, onClose, onSaved }) {
  const [name, setName] = useState(device.name || '');
  const [location, setLocation] = useState(device.location || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.devices.patch(device.id, {
        name: name.trim(),
        location: location.trim() || null,
      });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-3">
        <header className="flex items-center justify-between">
          <h3 className="font-semibold text-cathay-700">Edit device</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl">×</button>
        </header>
        <label className="block">
          <span className="text-sm text-slate-700">Name</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="text-sm text-slate-700">Location</span>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
          />
        </label>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded border">Cancel</button>
          <button disabled={busy} className="bg-cathay-500 hover:bg-cathay-600 text-white text-sm px-3 py-1.5 rounded">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

// Shows the per-device mTLS client-certificate expiry so admins can
// re-issue profiles before they lapse.
//   - null              → "Never issued" (slate)
//   - >30 days away     → "Expires YYYY-MM-DD" (green)
//   - ≤30 days away     → "Expires YYYY-MM-DD ⚠" (amber)
//   - past now          → "Expired YYYY-MM-DD" (red)
const MS_PER_DAY = 24 * 60 * 60 * 1000;
function CertExpiryBadge({ expiresAt }) {
  if (!expiresAt) {
    return <span className="text-xs text-slate-400">Never issued</span>;
  }
  const t = new Date(expiresAt).getTime();
  if (!Number.isFinite(t)) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const daysLeft = Math.floor((t - Date.now()) / MS_PER_DAY);
  const dateStr = fmtDate(expiresAt);
  let cls = 'bg-green-100 text-green-800';
  let label = `Expires ${dateStr}`;
  if (daysLeft < 0) {
    cls = 'bg-red-100 text-red-800';
    label = `Expired ${dateStr}`;
  } else if (daysLeft <= 30) {
    cls = 'bg-amber-100 text-amber-800';
    label = `Expires ${dateStr} ⚠`;
  }
  return (
    <span
      className={'px-2 py-0.5 rounded text-xs ' + cls}
      title={daysLeft < 0 ? 'Profile expired — re-issue ASAP' : `${daysLeft} day(s) remaining`}
    >
      {label}
    </span>
  );
}

// Origin of the iPad PWA. The admin runs on a DIFFERENT subdomain
// (ipt.cxfop.com) from the kiosk PWA (iptipad.cxfop.com), so we cannot
// use window.location.origin here. Overridable per-deployment via a Vite
// build env; defaults to the production iPad host.
const IPAD_ORIGIN = (import.meta.env.VITE_IPAD_ORIGIN || 'https://iptipad.cxfop.com').replace(/\/+$/, '');

// Builds the full kiosk recovery URL for a device. The 5-char token rides
// in the path (/v3/<TOKEN>) so the iPad PWA can re-pair itself after iOS
// wipes IndexedDB — the Home Screen URL survives data eviction; IDB does
// not. Returns null if no token has been issued yet.
function kioskUrlFor(d) {
  if (!d?.kiosk_token) return null;
  return `${IPAD_ORIGIN}/v3/${d.kiosk_token}`;
}

// Two successful bootstraps within this window strongly suggest two
// iPads sharing one kiosk URL (a legitimate device re-pairs at most
// once per iOS data-wipe — typically days/weeks apart).
const KIOSK_RAPID_REPAIR_MS = 30 * 60 * 1000;

function kioskRapidRepair(device) {
  const last = device?.kiosk_last_bootstrap_at;
  const prev = device?.kiosk_prev_bootstrap_at;
  if (!last || !prev) return false;
  const gap = new Date(last).getTime() - new Date(prev).getTime();
  return Number.isFinite(gap) && gap >= 0 && gap < KIOSK_RAPID_REPAIR_MS;
}

function KioskUrlCell({ device, isAdmin, onGenerate, onCopy, onSetActive }) {
  const url = kioskUrlFor(device);
  if (!url) {
    if (!isAdmin) return <span className="text-xs text-slate-400">—</span>;
    return (
      <button
        onClick={onGenerate}
        className="px-2 py-1 text-xs rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
        title="Generate a 5-character kiosk URL token. Bookmark/install this URL on the iPad — when iOS wipes site data, the PWA reads ?t= from this URL to recover automatically."
      >
        Generate URL
      </button>
    );
  }

  const active = device.kiosk_token_active !== false;
  const count = device.kiosk_bootstrap_count || 0;
  const lastAt = device.kiosk_last_bootstrap_at;
  const lastIp = device.kiosk_last_bootstrap_ip;
  const rapid = kioskRapidRepair(device);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <code
          className={
            'px-2 py-0.5 rounded text-xs font-mono ' +
            (active ? 'bg-slate-100 text-slate-800' : 'bg-slate-200 text-slate-400 line-through')
          }
          title={active ? url : `${url} (disabled)`}
        >
          {device.kiosk_token}
        </code>
        {!active && (
          <span className="px-1.5 py-0.5 text-[10px] rounded bg-slate-300 text-slate-700 uppercase tracking-wide">
            Disabled
          </span>
        )}
        {active && (
          <button
            onClick={onCopy}
            className="px-2 py-0.5 text-xs rounded bg-blue-100 hover:bg-blue-200 text-blue-800"
            title="Copy the full kiosk URL to clipboard"
          >
            Copy URL
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => onSetActive(!active)}
            className={
              'px-2 py-0.5 text-xs rounded ' +
              (active
                ? 'bg-red-100 hover:bg-red-200 text-red-800'
                : 'bg-green-100 hover:bg-green-200 text-green-800')
            }
            title={
              active
                ? 'Disable this URL (stops auto-recovery) without changing the token'
                : 'Re-enable this URL'
            }
          >
            {active ? 'Disable' : 'Enable'}
          </button>
        )}
        {isAdmin && (
          <button
            onClick={onGenerate}
            className="px-2 py-0.5 text-xs rounded bg-amber-100 hover:bg-amber-200 text-amber-800"
            title="Regenerate the token. WARNING: this invalidates the previous URL — the iPad must be re-installed with the new URL."
          >
            Regenerate
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        <span title="Lifetime successful URL→token exchanges">
          {count} recover{count === 1 ? 'y' : 'ies'}
        </span>
        {lastAt && (
          <span title={lastIp ? `Last source IP: ${lastIp}` : undefined}>
            · last {fmtDateTime(lastAt)}{lastIp ? ` (${lastIp})` : ''}
          </span>
        )}
        {rapid && (
          <span
            className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-medium"
            title="Two recoveries happened within 30 minutes — this usually means two iPads are sharing the same kiosk URL. Investigate, then Disable or Regenerate."
          >
            ⚠ rapid re-pairs
          </span>
        )}
      </div>
    </div>
  );
}
